<script setup lang="ts">
import Popover from '@/components/Popover.vue'
import LogoFull from '@/assets/images/logo-full.svg?component'
import Monogram from '@/assets/images/monogram.svg?component'
import Account from '@/assets/icons/account.svg?component'
import Basket from '@/assets/icons/basket.svg?component'
import IconChevron from '@/assets/icons/chevron.svg?component'
import { MENU_ACCOUNT } from '@/constants/menu-account'

defineProps<{
  cartCount: number
  hasBackButton?: boolean
  location?: string
  isSubscriber: boolean
  hideNavigation?: boolean
  showOutOfStock?: boolean
}>()

const emit = defineEmits<{
  (e: 'back'): void
  (e: 'goToCart'): void
  (e: 'goToHome'): void
  (e: 'goToUpcomingOrders'): void
  (e: 'goToOutOfStock'): void
  (e: 'goToYourProducts'): void
  (e: 'toggleMenuAccountMobile'): void
}>()
</script>

<template>
  <div class="MainHeader">
    <div class="MainHeader__Left">
      <div class="MainHeader__MobileNavExpander">
        <div
          v-if="hasBackButton"
          class="MainHeader__NavButton"
          @click="emit('back')"
        >
          <span><IconChevron class="Header__IconChevron" />Back</span>
        </div>
      </div>
      <div class="MainHeader__DesktopLogo">
        <a href="/"><LogoFull></LogoFull></a>
      </div>
    </div>
    <div class="MainHeader__Center">
      <div class="MainHeader__MobileLogo">
        <a href="/"><Monogram></Monogram></a>
      </div>
      <ul v-if="!hideNavigation" class="MainHeader__DesktopNav">
        <li
          :class="{
            'is-active': location === 'home',
          }"
        >
          <a href="/" @click.prevent="emit('goToHome')">Home</a>
        </li>
        <li
          v-if="isSubscriber"
          :class="{
            'is-active': location === 'upcoming-orders',
          }"
        >
          <a
            href="/upcoming-orders"
            @click.prevent="emit('goToUpcomingOrders')"
          >
            Upcoming orders
          </a>
        </li>
        <li
          v-if="isSubscriber && showOutOfStock"
          :class="{
            'is-active': location === 'out-of-stock',
          }"
        >
          <a href="/out-of-stock" @click.prevent="emit('goToOutOfStock')">
            Out of stock
          </a>
        </li>
        <li
          v-if="isSubscriber"
          :class="{
            'is-active': location === 'your-products',
          }"
        >
          <a
            href="/product-subscriptions"
            @click.prevent="emit('goToYourProducts')"
          >
            Your products
          </a>
        </li>
      </ul>
    </div>
    <div class="MainHeader__Right">
      <div class="MainHeader__NavButton">
        <div
          class="MainHeader__AccountButton hide-on-desktop"
          @click="emit('toggleMenuAccountMobile')"
        >
          <Account width="24" height="24"></Account>
        </div>
        <div class="hide-on-mobile">
          <Popover :offset-y="10" :offset-x="40">
            <template #trigger>
              <Account width="24" height="24" />
            </template>
            <template #content>
              <div class="AccountPopover">
                <ul>
                  <li
                    v-for="item in MENU_ACCOUNT.filter(
                      (item) => isSubscriber || item.allowedNonSubscriber,
                    )"
                    :key="item.label"
                  >
                    <a :href="item.href">{{ item.label }}</a>
                  </li>
                </ul>
              </div>
            </template>
          </Popover>
        </div>
      </div>
      <div
        v-if="isSubscriber"
        class="MainHeader__NavButton"
        @click="emit('goToCart')"
      >
        <Basket width="24" height="24"></Basket>
        <span v-if="cartCount" class="MainHeader__CartCount">
          {{ cartCount }}
        </span>
      </div>
    </div>
  </div>
</template>

<style>
.MainHeader {
  padding: 16px;
  display: flex;
  align-items: center;
  justify-content: flex-start;
  max-width: 1144px;
  min-height: 72px;

  @media (--mq-tablet-and-up) {
    max-width: 100%;
    padding: 32px 40px;
  }
}

.MainHeader__Right,
.MainHeader__Left {
  flex-grow: 0;
  display: flex;
  gap: 4px;
  min-width: 100px;
}

.MainHeader__Right {
  justify-content: flex-end;
}

.MainHeader__Center {
  flex-grow: 1;
  display: flex;
  align-items: center;
  justify-content: center;
}

.MainHeader__DesktopLogo a,
.MainHeader__MobileLogo a {
  color: var(--color-forest-100);
}

.MainHeader__DesktopLogo {
  display: none;
}

.MainHeader__DesktopNav {
  display: none;
}

.MainHeader__DesktopNav .is-active {
  border-radius: 48px;
  background: var(--Lichen-100, #e4e8e7);
}

.Header__IconChevron {
  margin-right: 5px;
  height: 12px;
  width: 12px;
}

@media (--mq-landscape-tablet-and-up) {
  .MainHeader__DesktopLogo {
    display: block;
    width: 102px;
    height: 36px;
  }

  .MainHeader__MobileNavExpander {
    display: none;
  }

  .MainHeader__MobileLogo {
    display: none;
  }

  .MainHeader__DesktopNav {
    display: block;
    display: flex;
    list-style: none;
    gap: 24px;

    & li {
      padding: 8px 16px;
    }

    & a {
      color: var(--color-forest-100);
    }
  }
}

.MainHeader__NavButton {
  padding: 6px;
  border-radius: var(--radius-small);
  transition: background-color ease-in-out 0.15s;
  cursor: pointer;
  position: relative;

  &:hover {
    cursor: pointer;
    background-color: rgba(var(--rgb-forest-100), 0.1);
  }
}

.MainHeader__CartCount {
  position: absolute;
  bottom: 3px;
  right: 3px;
  background-color: var(--color-forest-100);
  color: var(--color-white);
  font-size: 10px;
  border-radius: 1000px;
  padding: 1px 5px;
}

.AccountPopover {
  width: 264px;
  padding: 6px 20px;

  & ul {
    display: flex;
    flex-direction: column;
    align-items: flex-start;
    list-style: none;
    margin: 0;
    padding: 0;
  }

  & li {
    width: 100%;
  }

  & li a {
    display: block;
    color: var(--color-forest-100);
    font-family: var(--font-family-rund);
    font-size: 16px;
    font-style: normal;
    font-weight: 700;
    padding: 12px 0;
  }
}
</style>
